/* Nuance theme — Cashé Atelier. No dependencies. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const routes = (window.theme && window.theme.routes) || {};
  const strings = (window.theme && window.theme.strings) || {};

  const fetchJSON = (url, opts = {}) =>
    fetch(url, {
      ...opts,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(opts.headers || {}) }
    }).then(async (r) => {
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.status) throw data;
      return data;
    });

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------- Focus trap + dialog base ---------- */

  class Overlay extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-close]')) this.close(); });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trap(e);
      });
    }
    focusables() {
      return [...this.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), textarea, select, [tabindex]:not([tabindex="-1"])')]
        .filter((el) => el.offsetParent !== null);
    }
    trap(e) {
      const f = this.focusables();
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.setAttribute('open', '');
        const target = this.querySelector('[autofocus], input[type="search"]') || this.focusables()[0];
        if (target) target.focus({ preventScroll: true });
      }));
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'true');
    }
    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      document.documentElement.style.overflow = '';
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'false');
      const done = () => { this.hidden = true; };
      reduceMotion ? done() : setTimeout(done, 550);
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
    }
  }

  customElements.define('menu-drawer', class extends Overlay {});

  /* ---------- Search overlay with predictive results ---------- */

  customElements.define('search-overlay', class extends Overlay {
    connectedCallback() {
      super.connectedCallback();
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('.search-overlay__results');
      let timer;
      this.input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.search(this.input.value.trim()), 220);
      });
    }
    search(q) {
      if (!q) { this.results.innerHTML = ''; return; }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const url = `${routes.predictive}?q=${encodeURIComponent(q)}&resources[type]=product,page&resources[limit]=6&section_id=predictive-search`;
      fetch(url, { signal: this.controller.signal })
        .then((r) => r.text())
        .then((html) => {
          const doc = parseHTML(html);
          const section = doc.querySelector('.shopify-section') || doc.body;
          this.results.innerHTML = section.innerHTML;
        })
        .catch(() => {});
    }
  });

  document.addEventListener('click', (e) => {
    const searchBtn = e.target.closest('[data-search-open]');
    if (searchBtn) {
      const overlay = document.querySelector('search-overlay');
      if (overlay) { e.preventDefault(); overlay.open(searchBtn); }
    }
    const menuBtn = e.target.closest('[data-menu-open]');
    if (menuBtn) document.getElementById('MenuDrawer').open(menuBtn);
    const cartBtn = e.target.closest('[data-cart-open]');
    if (cartBtn && window.theme.cartType === 'drawer') {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) { e.preventDefault(); drawer.open(cartBtn); }
    }
  });

  /* ---------- Header scroll state ---------- */

  const onScroll = () => document.body.classList.toggle('is-scrolled', window.scrollY > 8);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  /* ---------- Cart ---------- */

  const Cart = {
    sections() {
      const s = ['cart-drawer'];
      if (document.querySelector('cart-page')) s.push(document.querySelector('cart-page').dataset.section);
      return s;
    },
    render(sections) {
      if (!sections) return;
      const drawer = document.querySelector('cart-drawer');
      if (drawer && sections['cart-drawer']) {
        const next = parseHTML(sections['cart-drawer']).querySelector('cart-drawer');
        ['.drawer__body', '.drawer__foot', '[data-cart-drawer-count]'].forEach((sel) => {
          const cur = drawer.querySelector(sel);
          const nxt = next.querySelector(sel);
          if (cur && nxt) cur.replaceWith(nxt);
          else if (!cur && nxt) drawer.querySelector('.drawer__body').after(nxt);
          else if (cur && !nxt) cur.remove();
        });
      }
      const page = document.querySelector('cart-page');
      if (page && sections[page.dataset.section]) {
        const next = parseHTML(sections[page.dataset.section]).querySelector('cart-page');
        if (next) page.innerHTML = next.innerHTML;
      }
    },
    updateCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
    },
    change(line, quantity, root) {
      root.classList.add('is-updating');
      const body = JSON.stringify({ line, quantity, sections: this.sections(), sections_url: window.location.pathname });
      return fetchJSON(`${routes.cartChange}.js`, { method: 'POST', body })
        .then((cart) => { this.render(cart.sections); this.updateCount(cart.item_count); })
        .catch((err) => {
          const box = root.querySelector('[data-cart-error]') || document.querySelector('[data-cart-error]');
          if (box) { box.textContent = (err && err.description) || strings.cartError; box.hidden = false; }
        })
        .finally(() => root.classList.remove('is-updating'));
    }
  };
  window.theme.Cart = Cart;

  const bindCartControls = (root) => {
    root.addEventListener('click', (e) => {
      const step = e.target.closest('[data-qty]');
      if (step) {
        const input = step.parentElement.querySelector('input');
        const next = Math.max(0, parseInt(input.value, 10) + parseInt(step.dataset.qty, 10));
        input.value = next;
        Cart.change(parseInt(input.dataset.lineQty, 10), next, root);
      }
      const rm = e.target.closest('[data-remove]');
      if (rm) Cart.change(parseInt(rm.dataset.remove, 10), 0, root);
    });
    root.addEventListener('change', (e) => {
      const input = e.target.closest('[data-line-qty]');
      if (input) Cart.change(parseInt(input.dataset.lineQty, 10), Math.max(0, parseInt(input.value, 10) || 0), root);
      const note = e.target.closest('[data-cart-note]');
      if (note) fetchJSON(`${routes.cartUpdate}.js`, { method: 'POST', body: JSON.stringify({ note: note.value }) }).catch(() => {});
    });
  };

  customElements.define('cart-drawer', class extends Overlay {
    connectedCallback() { super.connectedCallback(); bindCartControls(this); }
  });
  customElements.define('cart-page', class extends HTMLElement {
    connectedCallback() { bindCartControls(this); }
  });
  customElements.define('quantity-input', class extends HTMLElement {});

  /* ---------- Product form ---------- */

  customElements.define('product-form', class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form || window.theme.cartType !== 'drawer') return;
      this.form.addEventListener('submit', (e) => this.submit(e));
    }
    submit(e) {
      e.preventDefault();
      const button = this.form.querySelector('[data-add]');
      const label = this.form.querySelector('[data-add-label]');
      const error = this.form.querySelector('[data-form-error]');
      if (button.disabled) return;
      button.setAttribute('aria-busy', 'true');
      const original = label.textContent;
      label.textContent = strings.adding;
      error.hidden = true;

      const data = new FormData(this.form);
      data.append('sections', Cart.sections().join(','));
      data.append('sections_url', window.location.pathname);

      fetch(`${routes.cartAdd}.js`, { method: 'POST', body: data, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } })
        .then((r) => r.json())
        .then((res) => {
          if (res.status) throw res;
          Cart.render(res.sections);
          return fetchJSON(`${routes.cart}.js`);
        })
        .then((cart) => {
          Cart.updateCount(cart.item_count);
          label.textContent = strings.added;
          const drawer = document.querySelector('cart-drawer');
          if (drawer) drawer.open(button);
          setTimeout(() => { label.textContent = original; }, 1600);
        })
        .catch((err) => {
          label.textContent = original;
          error.textContent = (err && err.description) || strings.error;
          error.hidden = false;
        })
        .finally(() => button.removeAttribute('aria-busy'));
    }
  });

  /* ---------- Variant picker (only for products with real variants) ---------- */

  customElements.define('variant-picker', class extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.addEventListener('change', () => this.update());
    }
    update() {
      const selected = [...this.querySelectorAll('fieldset')].map((fs) => fs.querySelector('input:checked').value);
      const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      const page = this.closest('product-page');
      const idInput = page.querySelector('[data-variant-id]');
      const add = page.querySelector('[data-add]');
      const addLabel = page.querySelector('[data-add-label]');
      if (!variant) {
        add.disabled = true; addLabel.textContent = strings.unavailable;
        return;
      }
      idInput.value = variant.id;
      const url = `${page.dataset.url}?variant=${variant.id}`;
      window.history.replaceState({}, '', url);
      fetch(`${url}&section_id=${page.dataset.section}`)
        .then((r) => r.text())
        .then((html) => {
          const doc = parseHTML(html);
          ['[data-price-wrap]', '[data-stock]', '.pdp__buy-price'].forEach((sel) => {
            const cur = page.querySelector(sel), nxt = doc.querySelector(sel);
            if (cur && nxt) cur.innerHTML = nxt.innerHTML;
          });
          add.disabled = !variant.available;
          addLabel.textContent = variant.available ? strings.addToCart : strings.soldOut;
          const media = variant.featured_media && page.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (media) media.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
        });
    }
  });

  /* ---------- Product page: gallery counter + sticky add to cart ---------- */

  customElements.define('product-page', class extends HTMLElement {
    connectedCallback() {
      const gallery = this.querySelector('[data-gallery]');
      const counter = this.querySelector('[data-gallery-current]');
      if (gallery && counter) {
        gallery.addEventListener('scroll', () => {
          const i = Math.round(gallery.scrollLeft / gallery.clientWidth) + 1;
          counter.textContent = i;
        }, { passive: true });
      }
      const bar = this.querySelector('[data-sticky-atc]');
      const buy = this.querySelector('[data-buy]');
      if (bar && buy && 'IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.classList.toggle('is-visible', show);
          bar.setAttribute('aria-hidden', String(!show));
          bar.querySelector('button').tabIndex = show ? 0 : -1;
        }).observe(buy);
      }
    }
  });

  /* ---------- Recommendations ---------- */

  customElements.define('product-recommendations', class extends HTMLElement {
    connectedCallback() {
      if (!('IntersectionObserver' in window)) return this.load();
      new IntersectionObserver((entries, obs) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        this.load();
      }, { rootMargin: '0px 0px 400px 0px' }).observe(this);
    }
    load() {
      fetch(this.dataset.url).then((r) => r.text()).then((html) => {
        const next = parseHTML(html).querySelector('product-recommendations');
        if (next && next.innerHTML.trim()) { this.innerHTML = next.innerHTML; reveal(this); }
      }).catch(() => {});
    }
  });

  /* ---------- Colour hero ---------- */

  customElements.define('colour-hero', class extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.hero__slide')];
      this.tabs = [...this.querySelectorAll('.hero__tab')];
      if (this.slides.length < 2) return;
      this.index = 0;
      this.interval = parseInt(this.dataset.interval, 10) || 6000;
      this.style.setProperty('--interval', `${this.interval}ms`);
      this.autoplay = this.hasAttribute('data-autoplay') && !reduceMotion && !window.Shopify?.designMode;

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => { this.go(i); this.stop(); });
        tab.addEventListener('keydown', (e) => {
          const dir = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
          if (!dir) return;
          e.preventDefault();
          const next = (i + dir + this.tabs.length) % this.tabs.length;
          this.go(next); this.stop(); this.tabs[next].focus();
        });
      });

      // Preload the next image once the first has painted.
      window.addEventListener('load', () => this.warm(1), { once: true });

      if (this.autoplay) {
        this.addEventListener('mouseenter', () => this.pause());
        this.addEventListener('mouseleave', () => this.resume());
        this.addEventListener('focusin', () => this.pause());
        this.addEventListener('focusout', () => this.resume());
        document.addEventListener('visibilitychange', () => (document.hidden ? this.pause() : this.resume()));
        new IntersectionObserver(([e]) => (e.isIntersecting ? this.resume() : this.pause())).observe(this);
        this.play();
      }

      document.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (i > -1) { this.go(i); this.stop(); }
      });
    }
    warm(i) {
      const img = this.slides[i % this.slides.length]?.querySelector('img[loading="lazy"]');
      if (img) img.loading = 'eager';
    }
    go(i) {
      if (i === this.index) return;
      const prev = this.slides[this.index];
      this.slides.forEach((s) => s.removeAttribute('data-prev'));
      prev.removeAttribute('data-active');
      prev.setAttribute('data-prev', '');
      prev.setAttribute('aria-hidden', 'true');
      prev.querySelectorAll('a.button').forEach((a) => (a.tabIndex = -1));

      const next = this.slides[i];
      next.removeAttribute('aria-hidden');
      next.querySelectorAll('a.button').forEach((a) => a.removeAttribute('tabindex'));
      // force reflow so the clip-path transition starts from the closed state
      void next.querySelector('.hero__media')?.offsetWidth;
      next.setAttribute('data-active', '');

      this.tabs.forEach((t, n) => {
        t.setAttribute('aria-selected', String(n === i));
        t.tabIndex = n === i ? 0 : -1;
      });
      const tab = this.tabs[i];
      if (tab) tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
      this.index = i;
      this.warm(i + 1);
      if (this.playing) this.restartProgress();
    }
    restartProgress() {
      this.classList.remove('is-playing');
      void this.offsetWidth;
      this.classList.add('is-playing');
    }
    play() {
      this.playing = true;
      this.restartProgress();
      clearInterval(this.timer);
      this.timer = setInterval(() => { if (!this.paused) this.go((this.index + 1) % this.slides.length); }, this.interval);
    }
    pause() { if (!this.playing) return; this.paused = true; this.classList.add('is-paused'); }
    resume() { if (!this.playing) return; this.paused = false; this.classList.remove('is-paused'); }
    stop() {
      this.playing = false;
      clearInterval(this.timer);
      this.classList.remove('is-playing', 'is-paused');
    }
  });

  /* ---------- Reel: autoplay videos in view, arrow buttons ---------- */

  customElements.define('reel-strip', class extends HTMLElement {
    connectedCallback() {
      const section = this.closest('.reel');
      const step = () => (this.querySelector('.reel__item')?.clientWidth || 260) + 12;
      section?.querySelector('[data-reel-next]')?.addEventListener('click', () => this.scrollBy({ left: step() * 2, behavior: 'smooth' }));
      section?.querySelector('[data-reel-prev]')?.addEventListener('click', () => this.scrollBy({ left: -step() * 2, behavior: 'smooth' }));
      if (reduceMotion || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting) { target.preload = 'auto'; target.play().catch(() => {}); }
          else target.pause();
        });
      }, { threshold: 0.6 });
      this.querySelectorAll('video').forEach((v) => { v.muted = true; io.observe(v); });
    }
  });

  /* ---------- Collection sort ---------- */

  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-autosubmit]');
    if (select) select.form.submit();
  });

  /* ---------- Reveal on scroll ---------- */

  const revealIO = ('IntersectionObserver' in window && document.body.classList.contains('has-reveal') && !window.Shopify?.designMode)
    ? new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          obs.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 })
    : null;

  function reveal(root = document) {
    root.querySelectorAll('.reveal:not(.is-in)').forEach((el, i) => {
      if (!revealIO) { el.classList.add('is-in'); return; }
      el.style.transitionDelay = `${Math.min(i % 4, 3) * 70}ms`;
      revealIO.observe(el);
    });
  }
  reveal();
  document.addEventListener('shopify:section:load', (e) => reveal(e.target));
})();
